import type PhotoSwipeLightbox from 'photoswipe/lightbox'
import type {
  MediaImageFragment,
  MediaVideoFragment,
} from '#build/graphql-operations'
import { setGallery } from 'vue-preview-imgs'
import { falsy } from '~/helpers/type'
import IconArrowLeft from '~/assets/symbols/chevron-left.svg?raw'
import IconArrowRight from '~/assets/symbols/chevron-right.svg?raw'
import IconCross from '~/assets/symbols/cross.svg?raw'

type SliderComponent = typeof import('~/components/Slider/index.vue').default

export type LightboxMediaItem =
  | ({ __typename: 'MediaImage' } & MediaImageFragment)
  | ({ __typename: 'MediaVideo'; id: string } & MediaVideoFragment)

type UseLightboxOptions = {
  slider?: Ref<InstanceType<SliderComponent> | null>
  showCaption?: boolean
}

export default function (
  id: string,
  mediaItems: LightboxMediaItem[],
  options?: UseLightboxOptions,
) {
  const route = useRoute()
  const router = useRouter()
  const config = useRuntimeConfig()

  const openLightbox = (index: number) => {
    router.push({ query: { ...route.query, gallery: id, index } })
  }

  const getItemAttributes = (index: number) => ({
    'data-id': index,
  })

  if (import.meta.server) {
    return { openLightbox, getItemAttributes }
  }

  const lightboxOpen = computed(() => route.query.gallery === id)

  const lightboxData = computed(() =>
    mediaItems
      .map((item, idx) => {
        if (item.__typename === 'MediaImage') {
          const hash = item.image?.file?.rokka?.hash
          if (!hash) return undefined

          const maxWidth = 5000
          const maxHeight = 5000
          const sourceWidth = item.image?.file?.rokka?.sourceWidth || 5000
          const sourceHeight = item.image?.file?.rokka?.sourceHeight || 5000
          const aspectRatio = sourceWidth / sourceHeight

          let w = sourceWidth
          let h = sourceHeight

          if (w > maxWidth || h > maxHeight) {
            if (aspectRatio > 1) {
              w = maxWidth
              h = Math.round(maxWidth / aspectRatio)
            } else {
              h = maxHeight
              w = Math.round(maxHeight * aspectRatio)
            }
          }

          if (sourceWidth <= maxWidth && sourceHeight <= maxHeight) {
            w = sourceWidth
            h = sourceHeight
          }

          let src = buildRokkaUrl(
            'fe_nuxt_fullscreen_preview',
            '',
            hash,
            (item.name || 'image.jpg').replace('.png', '.jpg'),
            config.public.rokkaHost,
            { w: maxWidth, h: maxHeight },
          )

          if (!src.includes('.jpg')) {
            src += '.jpg'
          }

          return {
            src,
            id: idx,
            alt: item.image?.alt,
            caption: item.caption,
            w,
            h,
          }
        } else if (item.__typename === 'MediaVideo') {
          const videoSrc = item.video?.entity?.fileUrl
          if (!videoSrc) return undefined
          return {
            id: idx,
            type: 'video' as const,
            videoSrc,
            w: 1080,
            h: 1080,
          }
        }
        return undefined
      })
      .filter(falsy),
  )

  const getInitialIndex = (): number => {
    const indexValue = route.query.index
    if (indexValue && typeof indexValue === 'string') {
      const index = parseInt(indexValue)
      if (
        !Number.isNaN(index) &&
        index >= 0 &&
        index < lightboxData.value.length
      ) {
        return index
      }
    }
    return 0
  }

  const lightbox: PhotoSwipeLightbox = setGallery({
    arrowPrevSVG: `<div>${IconArrowLeft}</div>`,
    arrowNextSVG: `<div>${IconArrowRight}</div>`,
    dataSource: lightboxData.value,
    showHideAnimationType: 'zoom',
    bgOpacity: 1,
    counter: false,
    zoom: false,
    closeSVG: IconCross,
  })

  lightbox.addFilter('thumbEl', (thumbEl, data) => {
    const galleryEl = document.getElementById(id)
    if (galleryEl) {
      const el = galleryEl.querySelector(
        `[data-id="${data.id}"] img, [data-id="${data.id}"] video`,
      )
      if (el instanceof HTMLElement) return el
    }
    return thumbEl || document.body
  })

  lightbox.addFilter('placeholderSrc', (placeholderSrc, slide) => {
    const galleryEl = document.getElementById(id)
    if (galleryEl) {
      const el = galleryEl.querySelector(`[data-id="${slide.data.id}"] img`)
      if (el instanceof HTMLImageElement) return el.currentSrc
    }
    return placeholderSrc
  })

  lightbox.on('contentLoad', (e: any) => {
    const { content } = e
    if (content?.data?.type !== 'video') return

    e.preventDefault()

    const el = document.createElement('div')
    el.className = 'pswp-video'

    const video = document.createElement('video')
    video.setAttribute('loop', '')
    video.setAttribute('muted', '')
    video.setAttribute('playsinline', '')
    video.muted = true
    const source = document.createElement('source')
    source.src = content.data.videoSrc
    source.type = 'video/mp4'
    video.appendChild(source)
    el.appendChild(video)

    content.element = el
  })

  lightbox.on('contentActivate', (e: any) => {
    const { content } = e
    if (content?.data?.type !== 'video') return
    const video = content.element?.querySelector('video')
    if (video) {
      video.muted = true
      video.play().catch(() => {})
    }
  })

  lightbox.on('contentDeactivate', (e: any) => {
    const { content } = e
    if (content?.data?.type !== 'video') return
    const video = content.element?.querySelector('video')
    if (video) video.pause()
  })

  lightbox.addFilter('isContentLoading', (isLoading: boolean, content: any) => {
    return content?.data?.type === 'video' ? false : isLoading
  })

  lightbox.addFilter(
    'isContentZoomable',
    (isZoomable: boolean, content: any) => {
      return content?.data?.type === 'video' ? false : isZoomable
    },
  )

  lightbox.on('change', () => {
    const index = lightbox.pswp?.currIndex || 0
    if (options?.slider?.value) {
      const flicking = options.slider.value.getFlicking()
      if (flicking) {
        const visibleRatio = flicking.getPanel(index)?.visibleRatio
        if (!visibleRatio) {
          flicking.moveTo(index, 0)
        }
      }
    }
    router.replace({ query: { ...route.query, index } })
  })

  lightbox.on('close', function () {
    const pswpEl = lightbox.pswp?.element
    if (pswpEl) {
      pswpEl.querySelectorAll('video').forEach((video) => {
        video.pause()
      })
    }

    router.push({
      query: { ...route.query, gallery: undefined, index: undefined },
    })
  })

  lightbox.on('uiRegister', function () {
    if (!lightbox.pswp?.ui) return

    lightbox.pswp.ui.registerElement({
      name: 'bulletsIndicator',
      className: 'pswp__bullets-indicator',
      appendTo: 'wrapper',
      onInit: (el, pswp) => {
        const bullets: HTMLDivElement[] = []
        let prevIndex = -1

        for (let i = 0; i < pswp.getNumItems(); i++) {
          const bullet = document.createElement('div')
          bullet.className = 'pswp__bullet'
          bullet.onclick = (e) => {
            if (e.target instanceof HTMLElement) pswp.goTo(i)
          }
          el.appendChild(bullet)
          bullets.push(bullet)
        }

        pswp.on('change', () => {
          if (prevIndex >= 0) {
            bullets[prevIndex]?.classList.remove('pswp__bullet--active')
          }
          bullets[pswp.currIndex]?.classList.add('pswp__bullet--active')
          prevIndex = pswp.currIndex
        })
      },
    })

    if (options?.showCaption) {
      lightbox.pswp.ui.registerElement({
        name: 'custom-caption',
        order: 9,
        isButton: false,
        appendTo: 'wrapper',
        html: 'Caption',
        onInit: (el) => {
          if (!lightbox.pswp) return
          lightbox.pswp.on('change', () => {
            const alt = lightbox.pswp?.currSlide?.data?.caption
            el.innerHTML = alt || ''
          })
        },
      })
    }
  })

  watch(lightboxOpen, (isOpen) =>
    isOpen ? lightbox.loadAndOpen(getInitialIndex()) : lightbox.pswp?.close(),
  )

  onMounted(() => {
    if (lightboxOpen.value) {
      lightbox.loadAndOpen(getInitialIndex())
    }
  })

  onBeforeUnmount(() => {
    if (lightbox) lightbox.destroy()
  })

  return { openLightbox, getItemAttributes }
}
